import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Select,
    Radio
} from 'antd';
import {v4 as uuidv4} from 'uuid';
import {authService, cashierService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const {Title, Text} = Typography;

const CashierEdit = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cashier, setCashier] = useState(null);
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [formData, setFormData] = useState({
        role: [],
        status: []
    });

    // Состояние для отслеживания предыдущего тела запроса
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchData();
        setPageTitle('Редактирование кассира');

        setRightContent(
            authService.hasPermission('pos:cashiers:edit') && (<Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Сохранить
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setCashier(null);
        };
    }, [setPageTitle, setRightContent, id]);

    useEffect(() => {
        if (cashier) {
            form.setFieldsValue(cashier);
        }
    }, [cashier, form]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Получаем данные кассира
            const cashierResponse = await cashierService.getCashierById(id);

            // Получаем данные для формы
            const formDataResponse = await cashierService.getFormData();

            if (cashierResponse.status === 'success' && formDataResponse.status === 'success') {
                setCashier(cashierResponse.data);
                setFormData(formDataResponse.data);
                console.log('Получены данные кассира:', cashierResponse.data);
            } else {
                message.error('Не удалось загрузить данные кассира');
                navigate('/cashiers');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Кассир не найден",
                    description: "Указанный кассир не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных кассира. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // Обработка сохранения данных кассира
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await cashierService.saveCashier(
                id,
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Кассир успешно сохранен');
                fetchData(); // Обновляем данные после сохранения
            } else {
                message.error(response.message || 'Ошибка при сохранении кассира');
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            message.error(error.message || 'Ошибка при сохранении кассира');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    const formatFullName = (l_name, f_name, m_name) => {
        const parts = [l_name, f_name, m_name].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : 'Не указано';
    };

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Кассир «{formatFullName(cashier.l_name, cashier.f_name, cashier.m_name)}»
            </Title>
            <div style={{marginBottom: 24}}>
                <Text>ID: <Text strong>{cashier.id}</Text></Text>
            </div>

            <Form
                form={form}
                layout="horizontal"
                initialValues={cashier}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Личные данные" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Фамилия"
                                name="l_name"
                                rules={[
                                    {required: true, message: 'Введите фамилию'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите фамилию" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Имя"
                                name="f_name"
                                rules={[
                                    {required: true, message: 'Введите имя'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите имя" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Отчество"
                                name="m_name"
                                rules={[
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите отчество" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Номер карты клиента"
                                name="customer_card_number"
                                rules={[
                                    {max: 16, message: 'Максимальная длина - 16 символов'}
                                ]}
                            >
                                <Input placeholder="Введите номер карты клиента" maxLength={16}/>
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="Системная информация" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Роль"
                                name="role"
                                rules={[
                                    {required: true, message: 'Выберите роль'}
                                ]}
                            >
                                <Select
                                    options={formData.role.map(roleItem => ({
                                        label: roleItem.label,
                                        value: roleItem.value
                                    }))}
                                    placeholder="Выберите роль"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Статус"
                                name="status"
                                rules={[
                                    {required: true, message: 'Выберите статус'}
                                ]}
                            >
                                <Radio.Group>
                                    {formData.status.map(statusItem => (
                                        <Radio.Button
                                            key={statusItem.value}
                                            value={statusItem.value}
                                        >
                                            {statusItem.label}
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default CashierEdit;